/**
 * Utilities for working with Google Tag Manager dataLayer
 */

import type { FlatMappedGtmEventPayload, MappedContentData, MappedGtmEventName } from './core/gtm.types';
import type { UserDto } from '@/queries/user/user.schema';
import { parseCookies } from 'nookies';
import { Env } from '@/consts/Env';
import { isStaging } from '@/libs/getEnvironment';
import { readCustomerSourceHeadersFromStorage } from '@/libs/userSource/customerSourceStorage';
import { sanitizeTrackingValue } from '@/libs/userSource/trackingValue';
import { MAPPED_GTM_EVENTS } from './core/constants';
import { gtmUserProfileStore } from './core/gtmUserProfileStore';

const DEFAULT_EVENT_CONTENT: Record<MappedGtmEventName, MappedContentData> = {
  user_created: { content_type: 'form', content_id: 'registration' },
  user_email_verified: { content_type: 'verification', content_id: 'email_verification' },
  user_kyc_started: { content_type: 'verification', content_id: 'kyc' },
  user_kyc_completed: { content_type: 'verification', content_id: 'kyc' },
  user_logged_in: { content_type: 'form', content_id: 'login' },
};
const BROWSER_ID_STORAGE_KEY = 'gtm_browser_id_v1';

function hasDataLayerAccess(): boolean {
  return isClientSide() && !!Env.NEXT_PUBLIC_GTM_ID;
}

function isClientSide(): boolean {
  return typeof window !== 'undefined';
}

function sanitizeEventPayload<T extends Record<string, unknown>>(payload: T): T {
  const sanitizedEntries = Object.entries(payload).filter(([key, value]) => {
    if (key === 'event' || key === 'event_id' || key === 'event_time') {
      return true;
    }

    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return true;
  });

  return Object.fromEntries(sanitizedEntries) as T;
}

function buildEventId(eventName: string): string {
  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `${eventName}_${Date.now()}_${randomSuffix}`;
}

function extractStringField(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function resolveTrackingId(candidates: Array<string | null | undefined>): string | undefined {
  for (const candidate of candidates) {
    const sanitized = sanitizeTrackingValue(candidate);
    if (sanitized) {
      return sanitized;
    }
  }

  return undefined;
}

function resolveAttributionFields(): Partial<FlatMappedGtmEventPayload> {
  if (!isClientSide()) {
    return {};
  }

  const cookies = parseCookies();
  const searchParams = new URL(window.location.href).searchParams;
  const storedHeaders = readCustomerSourceHeadersFromStorage();

  return sanitizeEventPayload({
    ttclid: resolveTrackingId([cookies.ttclid, cookies._ttclid, storedHeaders['x-ttclid'], searchParams.get('ttclid')]),
    aclid: resolveTrackingId([cookies.aclid, cookies._aclid, searchParams.get('aclid')]),
    clickid: resolveTrackingId([cookies.click_id, cookies.clickid, storedHeaders['x-click-id'], searchParams.get('click_id'), searchParams.get('clickid')]),
    gclid: resolveTrackingId([cookies.gclid, cookies._gclid, storedHeaders['x-gclid'], searchParams.get('gclid')]),
    fbc: resolveTrackingId([cookies._fbc, cookies.fbc, storedHeaders['x-fbc'], searchParams.get('fbc')]),
  });
}

function generateBrowserId(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `browser_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function resolveBrowserId(): string | undefined {
  if (!isClientSide()) {
    return undefined;
  }

  let cachedBrowserId: string | undefined;

  try {
    cachedBrowserId = extractStringField(window.localStorage.getItem(BROWSER_ID_STORAGE_KEY));
  } catch {
    // Ignore storage errors.
  }

  const resolvedBrowserId = cachedBrowserId ?? generateBrowserId();

  try {
    window.localStorage.setItem(BROWSER_ID_STORAGE_KEY, resolvedBrowserId);
  } catch {
    // Ignore storage errors.
  }

  return resolvedBrowserId;
}

function pushEvent(payload: FlatMappedGtmEventPayload): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * @deprecated Prefer pushCustomGtmEventToDataLayer() and pass flat payload fields.
 */
export function pushToDataLayer(
  eventName: string,
  eventParams: Record<string, unknown> = {},
): void {
  if (!hasDataLayerAccess()) {
    return;
  }

  const eventData = sanitizeEventPayload({
    event: eventName,
    event_id: buildEventId(eventName),
    event_time: Math.floor(Date.now() / 1000),
    ...resolveAttributionFields(),
    browser_id: resolveBrowserId(),
    ...eventParams,
  });

  pushEvent(eventData as FlatMappedGtmEventPayload);

  if (isStaging()) {
    // eslint-disable-next-line no-console
    console.log('[GTM] Event pushed to dataLayer:', eventData);
  }
}

type PushCustomGtmEventOptions = {
  eventName: MappedGtmEventName;
  user?: Partial<UserDto>;
  auth_source?: string;
};

export function pushCustomGtmEventToDataLayer(
  eventName: MappedGtmEventName,
  user?: Partial<UserDto>,
): void;
export function pushCustomGtmEventToDataLayer(options: PushCustomGtmEventOptions): void;
export function pushCustomGtmEventToDataLayer(
  eventOrOptions: MappedGtmEventName | PushCustomGtmEventOptions,
  userArg?: Partial<UserDto>,
): void {
  const { eventName, user, auth_source } = typeof eventOrOptions === 'string'
    ? { eventName: eventOrOptions, user: userArg, auth_source: undefined }
    : eventOrOptions;

  if (!MAPPED_GTM_EVENTS.includes(eventName)) {
    if (isStaging()) {
      console.warn(`[GTM] Attempted to push unmapped event "${eventName}" to dataLayer. This event will not be sent. Please use one of the predefined events: ${MAPPED_GTM_EVENTS.join(', ')}`);
    }
    return;
  }

  if (!hasDataLayerAccess()) {
    return;
  }

  const eventId = buildEventId(eventName);
  const defaultContent = DEFAULT_EVENT_CONTENT[eventName] ?? {};

  const eventData = sanitizeEventPayload({
    event: eventName,
    event_id: eventId,
    event_time: Math.floor(Date.now() / 1000),
    ...gtmUserProfileStore.resolveAnalyticsUserFields(user),
    ...resolveAttributionFields(),
    browser_id: resolveBrowserId(),
    content_id: defaultContent.content_id,
    content_type: defaultContent.content_type,
    auth_source,
  }) as FlatMappedGtmEventPayload;

  pushEvent(eventData);

  if (isStaging()) {
    // eslint-disable-next-line no-console
    console.log('[GTM] Custom event pushed to dataLayer:', eventData);
  }
}
